import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { Empty } from "@/components/kit";

export const MovementRow = ({ m, showProduct }) => (
  <div className="flex items-center gap-3 py-2.5 border-t border-[#271E56] text-sm">
    {m.type === "entrada" ? <ArrowDownCircle className="w-4 h-4 text-emerald-400 shrink-0" /> : <ArrowUpCircle className="w-4 h-4 text-rose-400 shrink-0" />}
    <div className="flex-1 min-w-0">
      {showProduct && <div className="text-white truncate">{m.product_name}</div>}
      <div className="text-slate-400 text-xs truncate">{m.note || (m.type === "entrada" ? "Entrada" : "Salida")}</div>
    </div>
    <span className={`mono ${m.type === "entrada" ? "text-emerald-300" : "text-rose-300"}`}>{m.type === "entrada" ? "+" : "−"}{m.qty}</span>
    <span className="text-xs text-slate-500 w-32 text-right">{new Date(m.date).toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" })}</span>
  </div>
);

// Registrar entradas (compras) / salidas y ver historial del producto
export const MovementsDialog = ({ product, onOpenChange, onSaved }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ type: "entrada", qty: 1, note: "" });
  const load = useCallback(() => product && api.get(`/products/${product.id}/movements`).then((r) => setList(r.data)), [product]);
  useEffect(() => {
    setF({ type: "entrada", qty: 1, note: "" });
    load();
  }, [load]);

  const save = async () => {
    try {
      await api.post("/movements", { product_id: product.id, type: f.type, qty: Number(f.qty), note: f.note });
      toast.success("Movimiento registrado");
      setF({ type: "entrada", qty: 1, note: "" });
      load();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#120E2E] border-[#271E56] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="movements-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display">Movimientos · {product?.name}</DialogTitle>
        </DialogHeader>
        {product?.type !== "servicio" && (
          <div className="grid grid-cols-2 sm:grid-cols-[150px_100px_1fr_auto] gap-2 items-end">
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entrada">Entrada (compra)</SelectItem>
                <SelectItem value="salida">Salida</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" min="1" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="movement-qty-input" />
            <Input className="col-span-2 sm:col-span-1" placeholder="Nota (proveedor, motivo…)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="movement-note-input" />
            <Button className="btn-neon col-span-2 sm:col-span-1" onClick={save} data-testid="movement-save-btn">Registrar</Button>
          </div>
        )}
        <div className="text-sm text-slate-400">Stock actual: <span className="mono text-white" data-testid="movement-current-stock">{product?.stock}</span></div>
        <div data-testid="movement-history">
          {list.map((m) => <MovementRow key={m.id} m={m} />)}
          {!list.length && <Empty text="Sin movimientos registrados" />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
